import { z } from 'zod'
import type { EntryKind, GameSkuView, Gender } from './index'
import { blindLevelCount, blindStructureSchema } from './blind-structure'
import { formatCurrency } from './format'
import { entryKindLabels, promotionOfferLabels } from './labels'

export const awardThresholdKinds = ['player_count', 'hand_count'] as const
export const awardTypes = ['point', 'ticket', 'voucher'] as const
export const hunterRewardTypes = ['point', 'ticket', 'voucher'] as const
/** select：出局时点选对手；count：出局时填写该玩家累计拿过的赏金张数 */
export const hunterEliminationModes = ['select', 'count'] as const
export type HunterEliminationMode = (typeof hunterEliminationModes)[number]
export const maxBountyCount = 10_000

/** 遗留倍率；新单写在明细上，结算只在空值时回退开关 */
export const promotionStackMultiplier = 2

export const promotionOfferKeys = ['first_entry_price', 'stack_double'] as const

export type PromotionOfferKey = (typeof promotionOfferKeys)[number]

export const promotionPriceOfferSchema = z.object({
  kind: z.literal('price'),
  scope: z.literal('first_entry'),
  priceAmount: z.number().int().min(0),
})

/** 已冻结场次仍可能是半价/免单折扣；新写入只用 priceAmount */
export const legacyPromotionPriceOfferSchema = z.object({
  kind: z.literal('price'),
  scope: z.literal('first_entry'),
  offBasisPoints: z.union([z.literal(5000), z.literal(10000)]),
})

export const promotionStackOfferSchema = z.object({
  kind: z.literal('stack'),
  multiplier: z.literal(2),
})

export const promotionOfferSchema = z.union([
  promotionPriceOfferSchema,
  legacyPromotionPriceOfferSchema,
  promotionStackOfferSchema,
])

export type PromotionPriceOffer = z.infer<typeof promotionPriceOfferSchema>
export type LegacyPromotionPriceOffer = z.infer<
  typeof legacyPromotionPriceOfferSchema
>
export type PromotionOffer = z.infer<typeof promotionOfferSchema>

export const doubleStackPromotionOffer: PromotionOffer = {
  kind: 'stack',
  multiplier: 2,
}

export const legacyHalfEntryPromotionOffer: LegacyPromotionPriceOffer = {
  kind: 'price',
  scope: 'first_entry',
  offBasisPoints: 5_000,
}

export const legacyFreeEntryPromotionOffer: LegacyPromotionPriceOffer = {
  kind: 'price',
  scope: 'first_entry',
  offBasisPoints: 10_000,
}

export function pricePromotionOffer(priceAmount: number): PromotionPriceOffer {
  return { kind: 'price', scope: 'first_entry', priceAmount }
}

export function halfEntryPromotionOffer(
  entryPrice: number,
): PromotionPriceOffer {
  return pricePromotionOffer(Math.floor(entryPrice / 2))
}

export function isLegacyPricePromotion(
  offer: PromotionOffer,
): offer is LegacyPromotionPriceOffer {
  return (
    offer.kind === 'price' &&
    'offBasisPoints' in offer &&
    !('priceAmount' in offer)
  )
}

export function toWritablePromotionOffer(
  offer: PromotionOffer,
  entryPrice: number,
): PromotionOffer {
  if (offer.kind === 'stack' || 'priceAmount' in offer) return offer
  if (offer.offBasisPoints === 10_000) return pricePromotionOffer(0)
  return halfEntryPromotionOffer(entryPrice)
}

export function promotionOfferKey(offer: PromotionOffer): PromotionOfferKey {
  return offer.kind === 'stack' ? 'stack_double' : 'first_entry_price'
}

export function promotionOfferLabel(offer: PromotionOffer): string {
  if (offer.kind === 'stack') return promotionOfferLabels.stack_double
  if (isLegacyPricePromotion(offer)) {
    return offer.offBasisPoints === 10_000
      ? promotionOfferLabels.first_entry_free
      : promotionOfferLabels.first_entry_half
  }
  if (offer.priceAmount === 0) return promotionOfferLabels.first_entry_free
  return `本场首次报名 ${formatCurrency(offer.priceAmount)}`
}

export function parsePromotionOffer(value: unknown): PromotionOffer | null {
  const parsed = promotionOfferSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

export function formatPromotionToggleCopy(input: {
  offer: PromotionOffer
  isActive: boolean
  gameName?: string
}): { title: string; description: string } {
  const name = promotionOfferLabel(input.offer)
  const prefix = input.gameName ? `${input.gameName} ` : ''
  if (input.offer.kind === 'stack') {
    return input.isActive
      ? {
          title: `关闭${name}`,
          description: `${prefix}之后的买码恢复标准计分量，已下单的双倍不受影响。`,
        }
      : {
          title: `开启${name}`,
          description: `${prefix}全场所有桌立即生效：顾客照付一份，买码计分翻倍。`,
        }
  }
  const effect = promotionPriceEffect(input.offer)
  return input.isActive
    ? {
        title: `关闭${name}`,
        description: `${prefix}之后的报名恢复原价。`,
      }
    : {
        title: `开启${name}`,
        description: `${prefix}全场所有桌立即生效：${effect}`,
      }
}

export function formatPromotionSeatHint(offer: PromotionOffer): string {
  if (offer.kind === 'stack')
    return `${promotionOfferLabel(offer)}进行中，本次买码计分翻倍`
  if (isFreeEntryPromotion(offer))
    return `${promotionOfferLabel(offer)}进行中，尚未入场的首次报名免单`
  if (isLegacyPricePromotion(offer))
    return `${promotionOfferLabel(offer)}进行中，尚未入场的首次报名半价`
  return `${promotionOfferLabel(offer)}进行中，尚未入场的首次报名按此价`
}

export function promotionAdjustmentAmount(
  price: number,
  offer: PromotionPriceOffer | LegacyPromotionPriceOffer,
): number {
  if (!Number.isInteger(price) || price <= 0) return 0
  if ('priceAmount' in offer) {
    if (!Number.isInteger(offer.priceAmount) || offer.priceAmount < 0) return 0
    return Math.max(0, price - offer.priceAmount)
  }
  return Math.min(price, Math.floor((price * offer.offBasisPoints) / 10_000))
}

function isFreeEntryPromotion(offer: PromotionOffer) {
  if (offer.kind !== 'price') return false
  if (isLegacyPricePromotion(offer)) return offer.offBasisPoints === 10_000
  return offer.priceAmount === 0
}

function promotionPriceEffect(
  offer: PromotionPriceOffer | LegacyPromotionPriceOffer,
) {
  if (isFreeEntryPromotion(offer))
    return '尚未入场的首次报名免单，不占比赛券。已入座的续场原价。'
  if (isLegacyPricePromotion(offer))
    return '尚未入场的首次报名半价。已入座的续场原价。'
  return `尚未入场的首次报名按 ${formatCurrency(offer.priceAmount)}。已入座的续场原价。`
}

/** 历史订单 Adjustment 名称；新客改为租户发券后不再新写这一类 */
export const newCustomerEntryAdjustmentName = '新客入场免费'

export const hunterAwardReason = '猎人奖励'

/** 计分量上限，保证应用层始终落在安全整数内 */
export const maxChipAmount = 1_000_000_000_000

export type AwardThresholdKind = (typeof awardThresholdKinds)[number]
export type AwardType = (typeof awardTypes)[number]
export type HunterRewardType = (typeof hunterRewardTypes)[number]

const positiveInt = (label: string) =>
  z
    .number({ error: `${label}必须是数字` })
    .int(`${label}必须是整数`)
    .positive(`${label}必须大于 0`)

const chipAmountSchema = (label: string) =>
  positiveInt(label).max(maxChipAmount, `${label}超出可用范围`)

export const hunterRewardSchema = z
  .object({
    awardType: z.enum(hunterRewardTypes),
    count: z.coerce
      .number({ error: '奖励数量必须是数字' })
      .int('奖励数量必须是整数')
      .positive('奖励数量必须大于 0'),
    voucherId: z.string().uuid('票券不合法').nullable(),
  })
  .superRefine((reward, context) => {
    if (reward.awardType === 'voucher' && reward.voucherId === null)
      context.addIssue({
        code: 'custom',
        path: ['voucherId'],
        message: '请选择猎人赏票券',
      })
    if (reward.awardType !== 'voucher' && reward.voucherId !== null)
      context.addIssue({
        code: 'custom',
        path: ['voucherId'],
        message: '积分和比赛券不能绑定票券模板',
      })
  })

export const bountyCountSchema = z
  .number({ error: '请输入赏金数量' })
  .int('赏金数量必须是整数')
  .min(0, '赏金数量不能为负')
  .max(maxBountyCount, '赏金数量过大')

export const hunterGameConfigSchema = z.object({
  isRequired: z.boolean(),
  /** 机会只在该玩家本场第一次报名出现；旧场次缺省为否 */
  isOnce: z.boolean().optional(),
  /** 猎人行最多两张比赛券；旧场次缺省为否 */
  allowsTwoTickets: z.boolean().optional(),
  /** 旧场次缺省为点选对手 */
  elimination: z.enum(hunterEliminationModes).optional(),
  rewards: z.array(hunterRewardSchema).min(1, '猎人赛至少一项奖励'),
})

export type HunterReward = z.infer<typeof hunterRewardSchema>
export type HunterGameConfig = z.infer<typeof hunterGameConfigSchema>

/** 常规赛：入场与续场各自一档计分，基础分与买入分段都来自计分结构 */
export const normalGameConfigSchema = z.object({
  kind: z.literal('normal'),
  smallBlind: positiveInt('前位基础分'),
  bigBlind: positiveInt('后位基础分'),
  entryChips: chipAmountSchema('入场计分量'),
  rebuyChips: chipAmountSchema('续场计分量'),
  blindStructure: blindStructureSchema,
  /** 每次报名只能一手；旧场次缺省为否 */
  signupThrottled: z.boolean().optional(),
  hunter: hunterGameConfigSchema.optional(),
  promotion: promotionOfferSchema.optional(),
})

/** 锦标赛：入场计分在 buyinChips，续场计分在 rebuyChips；旧场次没有续场档时读入场那一档 */
export const tournamentGameConfigSchema = z.object({
  kind: z.literal('tournament'),
  buyinChips: chipAmountSchema('入场计分量'),
  rebuyChips: chipAmountSchema('续场计分量').optional(),
  blindStructure: blindStructureSchema,
  signupThrottled: z.boolean().optional(),
  hunter: hunterGameConfigSchema.optional(),
  promotion: promotionOfferSchema.optional(),
})

export const gameConfigSchema = z
  .discriminatedUnion('kind', [
    normalGameConfigSchema,
    tournamentGameConfigSchema,
  ])
  .superRefine((config, context) => {
    if (config.kind === 'normal' && config.bigBlind < config.smallBlind)
      context.addIssue({
        code: 'custom',
        path: ['bigBlind'],
        message: '后位基础分不能小于前位基础分',
      })
    if (blindLevelCount(config.blindStructure.entries) === 0)
      context.addIssue({
        code: 'custom',
        path: ['blindStructure', 'entries'],
        message: '至少需要一个计分级别',
      })
  })

export type NormalGameConfig = z.infer<typeof normalGameConfigSchema>
export type TournamentGameConfig = z.infer<typeof tournamentGameConfigSchema>
export type GameConfig = NormalGameConfig | TournamentGameConfig

export function parseGameConfig(value: unknown): GameConfig {
  return gameConfigSchema.parse(value)
}

export function hunterConfigOf(config: GameConfig): HunterGameConfig | null {
  return config.hunter ?? null
}

/** 缺省与旧场次都是点选处决对手 */
export function hunterEliminationOf(
  hunter: HunterGameConfig | null | undefined,
): HunterEliminationMode {
  return hunter?.elimination === 'count' ? 'count' : 'select'
}

export function toHunterElimination(
  value: string | null | undefined,
): HunterEliminationMode {
  return value === 'count' ? 'count' : 'select'
}

export function declaresHunterBounty(
  hunter: HunterGameConfig | null | undefined,
) {
  return hunterEliminationOf(hunter) === 'count'
}

export function promotionOf(config: GameConfig): PromotionOffer | null {
  return config.promotion ?? null
}

export type HunterChoice = 'none' | 'required' | 'normal_only' | 'choose'

export function isSignupThrottled(config: GameConfig): boolean {
  return config.signupThrottled === true
}

/** 节流续场时，在座不能再报名，裁判点出局后才能继续 */
export function isSignupHeldInPlay(
  config: GameConfig,
  playerStatus: string | null,
): boolean {
  return isSignupThrottled(config) && playerStatus === 'seated'
}

/** 两张比赛券只在猎人不同价（自选）时生效，全员同价不放开 */
export function allowsHunterTwoTickets(
  hunter: HunterGameConfig | null | undefined,
): boolean {
  return Boolean(hunter && !hunter.isRequired && hunter.allowsTwoTickets)
}

export function resolveHunterChoice(input: {
  hunter: HunterGameConfig | null | undefined
  /** 当前是否持有悬赏。有悬赏时不同价只走普通规格 */
  hasBounty: boolean
}): HunterChoice {
  if (!input.hunter) return 'none'
  if (input.hunter.isRequired) return 'required'
  if (input.hasBounty) return 'normal_only'
  return 'choose'
}

export function hunterLockedReason(_input: { hasBounty: boolean }): string {
  return '已持有悬赏，按普通续场计价'
}

export function resolveIsHunter(input: {
  hunter: HunterGameConfig | null | undefined
  hasBounty: boolean
  requested?: boolean
}): { isHunter: boolean } | { error: string } {
  const choice = resolveHunterChoice(input)
  if (choice === 'none') {
    if (input.requested === true) return { error: '本场未开启猎人赛' }
    return { isHunter: false }
  }
  if (choice === 'required') {
    if (input.requested === false) return { error: '本场全员猎人赛' }
    return { isHunter: true }
  }
  if (choice === 'normal_only') return { isHunter: input.requested === true }
  if (input.requested === undefined) return { error: '请选择普通赛或猎人赛' }
  return { isHunter: input.requested }
}

/** 全员或已锁普通时不进规格抽屉，其余自己选 */
export function hunterNeedsSkuPicker(choice: HunterChoice) {
  return choice === 'choose'
}

export function resolvePickedHunter(choice: HunterChoice, picked: boolean) {
  if (choice === 'required') return true
  if (choice === 'choose') return picked
  return false
}

export function vouchersForHand<T>(
  available: {
    entry: T[]
    rebuy: T[]
    hunterEntry: T[]
    hunterRebuy: T[]
  },
  kind: EntryKind,
  isHunter: boolean,
): T[] {
  if (isHunter)
    return kind === 'entry' ? available.hunterEntry : available.hunterRebuy
  return kind === 'entry' ? available.entry : available.rebuy
}

export function chipAmountForKind(config: GameConfig, kind: EntryKind): number {
  if (config.kind === 'tournament') {
    if (kind === 'rebuy') return config.rebuyChips ?? config.buyinChips
    return config.buyinChips
  }
  return kind === 'entry' ? config.entryChips : config.rebuyChips
}

export interface GenderSkuPrice {
  skuId: string
  name: string
  price: number
}

export interface GenderPricing {
  entry: GenderSkuPrice
  rebuy: GenderSkuPrice
  femaleEntry: GenderSkuPrice | null
  femaleRebuy: GenderSkuPrice | null
  hunterEntry: GenderSkuPrice | null
  hunterRebuy: GenderSkuPrice | null
  hunterFemaleEntry: GenderSkuPrice | null
  hunterFemaleRebuy: GenderSkuPrice | null
}

function toGenderSkuPrice(
  sku: { id: string; name: string; priceAmount: number } | null | undefined,
): GenderSkuPrice | null {
  if (!sku) return null
  return { skuId: sku.id, name: sku.name, price: sku.priceAmount }
}

/**
 * 按性别选定实际成交的 SKU 与价格。
 * 女士档缺省或性别未知时一律走标准档，`unknown` 不单独定价。
 */
export function resolveGenderSku(
  pricing: GenderPricing,
  kind: EntryKind,
  gender: Gender,
  isHunter = false,
): GenderSkuPrice {
  if (isHunter) {
    const hunter = resolveHunterSku(pricing, kind, gender)
    if (!hunter) throw new RangeError('猎人规格不存在')
    return hunter
  }
  if (gender === 'female') {
    const female = kind === 'entry' ? pricing.femaleEntry : pricing.femaleRebuy
    if (female) return female
  }
  return kind === 'entry' ? pricing.entry : pricing.rebuy
}

export function resolveHunterSku(
  pricing: GenderPricing,
  kind: EntryKind,
  gender: Gender,
): GenderSkuPrice | null {
  if (gender === 'female') {
    const female =
      kind === 'entry' ? pricing.hunterFemaleEntry : pricing.hunterFemaleRebuy
    if (female) return female
  }
  return kind === 'entry' ? pricing.hunterEntry : pricing.hunterRebuy
}

export function hasGenderPricing(pricing: {
  femaleEntry: unknown
  femaleRebuy: unknown
}): boolean {
  return pricing.femaleEntry !== null && pricing.femaleRebuy !== null
}

export function genderPricingFromSkus(skus: GameSkuView): GenderPricing {
  return {
    entry: {
      skuId: skus.entry.id,
      name: skus.entry.name,
      price: skus.entry.priceAmount,
    },
    rebuy: {
      skuId: skus.rebuy.id,
      name: skus.rebuy.name,
      price: skus.rebuy.priceAmount,
    },
    femaleEntry: toGenderSkuPrice(skus.femaleEntry),
    femaleRebuy: toGenderSkuPrice(skus.femaleRebuy),
    hunterEntry: toGenderSkuPrice(skus.hunterEntry),
    hunterRebuy: toGenderSkuPrice(skus.hunterRebuy),
    hunterFemaleEntry: toGenderSkuPrice(skus.hunterFemaleEntry),
    hunterFemaleRebuy: toGenderSkuPrice(skus.hunterFemaleRebuy),
  }
}

/** 展示用：按已知性别挑出实际生效的两档 SKU；性别未知时给标准档 */
export function displayGenderSkus(
  skus: GameSkuView,
  gender: Gender | null,
  isHunter = false,
) {
  if (isHunter) {
    if (gender === 'female' && skus.hunterFemaleEntry && skus.hunterFemaleRebuy)
      return { entry: skus.hunterFemaleEntry, rebuy: skus.hunterFemaleRebuy }
    if (skus.hunterEntry && skus.hunterRebuy)
      return { entry: skus.hunterEntry, rebuy: skus.hunterRebuy }
  }
  if (gender === 'female' && skus.femaleEntry && skus.femaleRebuy)
    return { entry: skus.femaleEntry, rebuy: skus.femaleRebuy }
  return { entry: skus.entry, rebuy: skus.rebuy }
}

/** 性别未填但赛事有女士价时，提示可以补全性别自动改价 */
export function shouldPromptGender(
  skus: GameSkuView,
  gender: Gender | null,
): boolean {
  if (!skus.femaleEntry || !skus.femaleRebuy) return false
  return gender === null || gender === 'unknown'
}

export interface GameTemplateAwardPrize {
  awardType: AwardType
  count: number
  /** 仅 `voucher` 必填；旧数据没有这个字段 */
  voucherId?: string | null
}

export interface GameTemplateAwardPlace {
  prizes: GameTemplateAwardPrize[]
}

export interface GameTemplateAward {
  threshold: number
  places: GameTemplateAwardPlace[]
}

export interface AwardVoucherOption {
  id: string
  name: string
}

export interface AwardTierSummary {
  heading: string
  lines: string[]
}

const tierOrdinals = [
  '一',
  '二',
  '三',
  '四',
  '五',
  '六',
  '七',
  '八',
  '九',
  '十',
] as const

export function toChipAmount(stackBb: number, bigBlind: number): number {
  return stackBb * bigBlind
}

/** 展示派生：绝对计分折算成后位基础分数，只用于文案 */
export function toStackBb(chips: number, bigBlind: number): number {
  return bigBlind > 0 ? Math.round(chips / bigBlind) : 0
}

export function resolveAwardTier(
  awards: GameTemplateAward[],
  actual: number,
): GameTemplateAward | null {
  let matched: GameTemplateAward | null = null
  for (const award of awards) {
    if (actual < award.threshold) continue
    if (!matched || award.threshold > matched.threshold) matched = award
  }
  return matched
}

export function awardRankLabel(index: number): string {
  const named = ['冠军', '亚军', '季军'] as const
  return named[index] ?? `第${index + 1}名`
}

export function awardTierLabel(index: number): string {
  const ordinal = tierOrdinals[index]
  return ordinal ? `第${ordinal}等级` : `第${index + 1}等级`
}

export function awardThresholdUnit(kind: AwardThresholdKind): '人' | '手' {
  return kind === 'hand_count' ? '手' : '人'
}

export function parseGameTemplateAwards(value: unknown): GameTemplateAward[] {
  if (!Array.isArray(value) || value.length === 0) return []
  if (!value.every(isAwardTier)) return []
  return value
}

export function defaultAwardPrize(): GameTemplateAwardPrize {
  return { awardType: 'point', count: 100 }
}

export function nextAwardTier(awards: GameTemplateAward[]): GameTemplateAward {
  if (awards.length === 0)
    return { threshold: 1, places: [{ prizes: [defaultAwardPrize()] }] }
  const highest = awards.reduce((best, item) =>
    item.threshold >= best.threshold ? item : best,
  )
  return {
    threshold: highest.threshold + 1,
    places: clonePlaces(highest.places),
  }
}

export function cloneAwardTier(tier: GameTemplateAward): GameTemplateAward {
  return { threshold: tier.threshold, places: clonePlaces(tier.places) }
}

export function nextAwardPlace(
  places: GameTemplateAwardPlace[],
): GameTemplateAwardPlace {
  const last = places[places.length - 1]
  return last ? clonePlace(last) : { prizes: [defaultAwardPrize()] }
}

/** 依次补积分、比赛券，再补本名额还没用上的票券 */
export function nextUnusedAwardPrize(
  existing: GameTemplateAwardPrize[],
  voucherIds: string[] = [],
): GameTemplateAwardPrize | null {
  if (!existing.some((prize) => prize.awardType === 'point'))
    return defaultAwardPrize()
  if (!existing.some((prize) => prize.awardType === 'ticket'))
    return { awardType: 'ticket', count: 1 }
  const voucherId = voucherIds.find(
    (id) => !existing.some((prize) => prize.voucherId === id),
  )
  return voucherId ? { awardType: 'voucher', count: 1, voucherId } : null
}

export function formatAwardTierSummary(
  tier: GameTemplateAward,
  kind: AwardThresholdKind,
  vouchers: AwardVoucherOption[] = [],
): AwardTierSummary {
  const unit = awardThresholdUnit(kind)
  return {
    heading: `在 ${tier.threshold} ${unit}时`,
    lines: tier.places.map((place, index) => {
      const prizes = place.prizes
        .map((prize) => formatAwardPrize(prize, vouchers))
        .join('；')
      return `${awardRankLabel(index)}获得 ${prizes}`
    }),
  }
}

export function awardVoucherIds(awards: GameTemplateAward[]): string[] {
  const ids = awards.flatMap((tier) =>
    tier.places.flatMap((place) =>
      place.prizes.flatMap((prize) =>
        prize.awardType === 'voucher' && prize.voucherId
          ? [prize.voucherId]
          : [],
      ),
    ),
  )
  return [...new Set(ids)]
}

export function remainingRebuys(
  limit: number | null,
  used: number,
): number | null {
  if (limit === null) return null
  return Math.max(limit - used, 0)
}

export function parseHunterRewards(value: unknown): HunterReward[] {
  const parsed = z.array(hunterRewardSchema).safeParse(value)
  return parsed.success ? parsed.data : []
}

export function emptySeatAvailableVouchers(): {
  entry: never[]
  rebuy: never[]
  hunterEntry: never[]
  hunterRebuy: never[]
} {
  return { entry: [], rebuy: [], hunterEntry: [], hunterRebuy: [] }
}

export function defaultHunterReward(): HunterReward {
  return { awardType: 'point', count: 100, voucherId: null }
}

export function formatHunterReward(reward: HunterReward): string {
  if (reward.awardType === 'point') return `${reward.count} 积分`
  if (reward.awardType === 'ticket') return `${reward.count} 张比赛券`
  return `${reward.count} 张票券`
}

/** 赛事目录下的 SKU 名。一个模板一件商品，SKU 名区分入场/续场、女士价与猎人 */
export function catalogSkuName(
  kind: EntryKind,
  isFemale: boolean,
  isHunter = false,
): string {
  const base = entryKindLabels[kind]
  const labeled = isHunter ? `猎人${base}` : base
  return isFemale ? `${labeled}（女士）` : labeled
}

export function visibleMotivations<
  Template extends {
    id: string
    motivationsEnabled: boolean
    motivationIds: string[]
  },
  Rule extends { id: string },
>(templates: Template[], rules: Rule[], gameTemplateId: string | null): Rule[] {
  if (!gameTemplateId) return []
  const template = templates.find((item) => item.id === gameTemplateId)
  if (!template?.motivationsEnabled) return []
  const allowed = new Set(template.motivationIds)
  return rules.filter((rule) => allowed.has(rule.id))
}

export function formatAwardPlaceSummary(
  place: GameTemplateAwardPlace,
  vouchers: AwardVoucherOption[] = [],
): string {
  return place.prizes
    .map((prize) => formatAwardPrize(prize, vouchers))
    .join('；')
}

function formatAwardPrize(
  prize: GameTemplateAwardPrize,
  vouchers: AwardVoucherOption[],
): string {
  if (prize.awardType === 'point') return `${prize.count} 积分`
  if (prize.awardType === 'ticket') return `${prize.count} 张比赛券`
  const voucher = vouchers.find((item) => item.id === prize.voucherId)
  return `${prize.count} 张${voucher?.name ?? '票券'}`
}

function clonePlaces(
  places: GameTemplateAwardPlace[],
): GameTemplateAwardPlace[] {
  return places.map(clonePlace)
}

function clonePlace(place: GameTemplateAwardPlace): GameTemplateAwardPlace {
  return { prizes: place.prizes.map((prize) => ({ ...prize })) }
}

function isAwardPrize(value: unknown): value is GameTemplateAwardPrize {
  if (typeof value !== 'object' || value === null) return false
  const prize = value as GameTemplateAwardPrize
  if (!awardTypes.includes(prize.awardType)) return false
  if (typeof prize.count !== 'number') return false
  if (prize.awardType === 'voucher') return typeof prize.voucherId === 'string'
  return prize.voucherId === undefined || prize.voucherId === null
}

function isAwardPlace(value: unknown): value is GameTemplateAwardPlace {
  if (typeof value !== 'object' || value === null) return false
  const place = value as GameTemplateAwardPlace
  return Array.isArray(place.prizes) && place.prizes.every(isAwardPrize)
}

function isAwardTier(value: unknown): value is GameTemplateAward {
  if (typeof value !== 'object' || value === null) return false
  const tier = value as GameTemplateAward
  return (
    typeof tier.threshold === 'number' &&
    Array.isArray(tier.places) &&
    tier.places.every(isAwardPlace)
  )
}
